import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DialogContent, Grid } from "@mui/material";
import { toast } from "react-toastify";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { DateObject } from "react-multi-date-picker";
import { LocaleContext } from "../../../../routing/LangRouter.js";

//import constants
import BASE_URL from "../../../../constants/baseUrls.js";

//import general components
import { BaseButton } from "../../../../components/common/base-button/base-button";
import BaseTextField from "../../../common/base-text-field/base-text-field";
import AutoCompleteComponent from "../../../common/base-select/autocomplete-component.js";
import SingleDatePicker from "../../../common/single-date-picker/single-date-picker.jsx";
import BaseDialog from "../../../common/base-dialog/base-dialog.jsx";
import { fixNumbers, navigateToLoginPage } from "../../../../utils/utility.js";

function AddPassengerModal({ getPassengerList, open, setOpen }) {
    //general variables
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const { locale } = useContext(LocaleContext);
    const { t } = useTranslation();
    const accessToken = localStorage.getItem("accessToken");
    const [loading, setLoading] = useState(false);
    const [isFocused, setIsFocused] = useState({});

    //request to get passenger list
    const [passengerItem, setPassengerItem] = useState([]);
    const getPassengerItem = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${BASE_URL}/api/v2/account/user/passenger/`, {
                params: {
                    pk: searchParams.get("id"),
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            setPassengerItem(response.data.data);
            setLoading(false);
        } catch (error) {
            toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
        }
    };

    //country list request
    const [countrySearchValue, setCountrySearchValue] = useState("");
    const [countryList, setCountryList] = useState([]);
    const getCountryList = async () => {
        const response = await axios.get(`${BASE_URL}/api/v2/basic/countries/`);
        setCountryList(response.data.data.list);
    };
    useEffect(() => {
        if (open) getCountryList();

        if (open && searchParams.get("id") && searchParams.get("action") === "edit") {
            getPassengerItem();
        }
    }, [open, searchParams]);

    //options gender
    const [genderSearchValue, setGenderSearchValue] = useState("");
    const genderOption = [
        { id: 1, name: t("male") },
        { id: 2, name: t("female") },
    ];

    //field's form
    const formList = [
        { id: 1, label: t("firstName"), name: "first_name", fieldType: "text", type: "input", dir: "rtl" },
        { id: 2, label: t("lastName"), name: "last_name", fieldType: "text", type: "input", dir: "rtl" },
        { id: 3, label: t("firstNameEn"), name: "english_first_name", fieldType: "text", type: "input", dir: "ltr" },
        { id: 4, label: t("lastNameEn"), name: "english_last_name", fieldType: "text", type: "input", dir: "ltr" },
        { id: 5, label: t("gender"), name: "gender", type: "select", options: genderOption, setInputValue: setGenderSearchValue },
        { id: 6, label: t("email"), name: "email", fieldType: "text", type: "input", dir: "ltr" },
        { id: 7, label: t("phoneNumber"), name: "phone_number", fieldType: "number", type: "input", dir: "ltr" },
        { id: 8, label: t("nationalCode"), name: "national_code", fieldType: "number", type: "input", dir: "ltr" },
        { id: 9, label: t("birthday"), name: "birthdate", type: "date", maxDate: new Date(), isPersianCalendar: true },
        { id: 10, label: t("nationality"), name: "nationality", type: "select", options: countryList, setInputValue: setCountrySearchValue },
        { id: 11, label: t("passport"), name: "passport_no", fieldType: "text", type: "input", dir: "ltr" },
        {
            id: 12,
            label: t("passportCountry"),
            name: "passport_issue_country",
            type: "select",
            options: countryList,
            setInputValue: setCountrySearchValue,
        },
        { id: 13, label: t("passportExpire"), name: "passport_expiry_date", type: "date", minDate: new Date(), isPersianCalendar: false },
    ];

    //handle form submit
    const {
        register,
        reset,
        handleSubmit,
        getValues,
        control,
        formState: { errors, dirtyFields },
    } = useForm({
        defaultValues: passengerItem,
        mode: "onChange",
        delayError: 500,
    });

    useEffect(() => {
        const action = searchParams.get("action");
        if (open && passengerItem && action === "edit") {
            reset(passengerItem);
        }
    }, [reset, open, searchParams, passengerItem]);

    const handleRegistration = data => {
        const newData = Object.keys(dirtyFields).reduce(
            (acc, dirtyField) => {
                if (dirtyField === "nationality" || dirtyField === "gender" || dirtyField === "passport_issue_country") {
                    return acc;
                }
                return {
                    ...acc,
                    [dirtyField]: fixNumbers(data?.[dirtyField]),
                };
            },
            {
                nationality: data?.country?.id,
                gender: data?.gender?.id,
                passport_issue_country: data?.passport_issue_country?.id,
            },
        );
        if (searchParams.get("action") === "edit") {
            editPassenger(newData);
        }
        if (searchParams.get("action") === "add") {
            addPassenger(newData);
        }
    };

    const addPassenger = async data => {
        try {
            setLoading(true);
            await axios.post(`${BASE_URL}/api/v2/account/user/passenger/`, data, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            handleSuccessResponse(t("successMessage"));
        } catch (error) {
            handleErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    const editPassenger = async data => {
        try {
            setLoading(true);
            await axios.patch(`${BASE_URL}/api/v2/account/user/passenger/`, data, {
                params: {
                    pk: passengerItem?.id,
                },
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    "Content-Type": "application/json",
                },
            });
            handleSuccessResponse(t("successMessage"));
        } catch (error) {
            handleErrorResponse(error);
        } finally {
            setLoading(false);
        }
    };

    const handleSuccessResponse = message => {
        toast.success(message, { toastId: "custom-id-yes" });
        getPassengerList();
        handleClose();
    };

    const handleErrorResponse = error => {
        if (error.response?.status === 401) {
            localStorage.clear();
            navigateToLoginPage(navigate, locale, location);
        } else {
            toast.error(t("Toast Messages.Error Occurred"), { toastId: "custom-id-yes" });
        }
    };

    const handleError = error => console.log(error);

    const registerOptions = {
        first_name: {
            required: `${t("firstName")} ${t("isRequired")}`,
            minLength: { value: 2, message: `${t("firstNameValidation1")}` },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: `${t("firstNameValidation2")}` },
        },
        last_name: {
            required: `${t("lastName")} ${t("isRequired")}`,
            minLength: { value: 2, message: `${t("firstNameValidation1")}` },
            pattern: { value: /^[\u0600-\u06FF\s]+$/, message: `${t("firstNameValidation2")}` },
        },
        english_first_name: {
            required: `${t("firstNameEn")} ${t("isRequired")}`,
            minLength: { value: 2, message: `${t("firstNameEnValidation1")}` },
            pattern: { value: /^[A-Za-z][A-Za-z0-9]*$/, message: `${t("firstNameENValidation2")}` },
        },
        english_last_name: {
            required: `${t("lastNameEn")} ${t("isRequired")}`,
            minLength: { value: 2, message: `${t("lastNameEnValidation1")}` },
            pattern: { value: /^[A-Za-z][A-Za-z0-9]*$/, message: `${t("lastNameEnValidation2")}` },
        },
        gender: { required: `${t("gender")} ${t("isRequired")}` },
        email: {
            pattern: {
                value: /^[a-zA-Z0-9_\u06F0-\u06F9.+-]+@[a-zA-Z0-9-]+\.[a-zA-Z]{2,}$/,
                message: `${t("email")} ${t("isWrong")}`,
            },
        },
        phone_number: {
            required: `${t("phoneNumber")} ${t("isRequired")}`,
            maxLength: { value: 11, message: `${t("phoneNumberValidation1")}` },
            minLength: { value: 11, message: `${t("phoneNumberValidation2")}` },
            pattern: { value: /^(09|\u06F0\u06F9)[0-9\u06F0-\u06F9]{9}$/, message: `${t("phoneNumberValidation3")}` },
        },
        national_code: {
            required: `${t("nationalCode")} ${t("isRequired")}`,
            validate: {
                validateNumber: nationalId => {
                    nationalId = fixNumbers(nationalId);
                    // Check length is 10
                    if (nationalId.length < 8 || 10 < nationalId.length) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }
                    //Check if all of the numbers are the same
                    if (
                        nationalId === "0000000000" ||
                        nationalId === "1111111111" ||
                        nationalId === "2222222222" ||
                        nationalId === "3333333333" ||
                        nationalId === "4444444444" ||
                        nationalId === "5555555555" ||
                        nationalId === "6666666666" ||
                        nationalId === "7777777777" ||
                        nationalId === "8888888888" ||
                        nationalId === "9999999999"
                    ) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    // STEP 00 : if nationalId.lenght==8 add two zero on the left
                    if (nationalId.length < 10) {
                        let zeroNeeded = 10 - nationalId.length;

                        let zeroString = "";
                        if (zeroNeeded == 2) {
                            zeroString = "00";
                        } else {
                            zeroString = "0";
                        }

                        nationalId = zeroString.concat(nationalId);
                    }

                    // STEP 1: Sum all numbers
                    let sum = 0;
                    for (let i = 0; i < 9; i++) {
                        sum += nationalId.charAt(i) * (10 - i);
                    }

                    // STEP 2: MOD ON 11
                    let mod = sum % 11;

                    // STEP 3: Check with 2
                    let finalValue;
                    if (mod >= 2) {
                        finalValue = 11 - mod;
                    } else {
                        finalValue = mod;
                    }

                    // STEP 4: Final Step check with control value
                    if (finalValue != nationalId.charAt(9)) {
                        return `${t("nationalCode")} ${t("isWrong")}`;
                    }

                    const foundNationalCode = getValues("national_code") === nationalId;

                    if (foundNationalCode && foundNationalCode.length > 1) {
                        return `${t("nationalCodeValidation")}`;
                    }

                    return true;
                },
            },
        },
        birthdate: {
            required: `${t("birthday")} ${t("isRequired")}`,
        },
        nationality: {
            required: `${t("country")} ${t("isRequired")}`,
        },
        passport_no: {
            pattern: { value: /^[A-Za-z]{1,2}[\u06F0-\u06F90-9]{6,9}$/, message: `${t("passport")} ${t("isWrong")}` },
        },
        passport_issue_country: {},
        passport_expiry_date: {},
    };

    //close modal
    const handleClose = () => {
        reset({
            first_name: "",
            last_name: "",
            english_first_name: "",
            english_last_name: "",
            gender: "",
            email: "",
            phone_number: "",
            national_code: "",
            birthdate: "",
            nationality: "",
            passport_no: "",
            passport_issue_country: "",
            passport_expiry_date: "",
        });
        setOpen(false);
        searchParams.delete("action");
        searchParams.delete("id");
        setSearchParams(searchParams);
        setPassengerItem([]);
        setIsFocused({});
    };

    return (
        <BaseDialog title={searchParams.get("action") === "edit" ? t("Passengers.Edit Passenger") : t("addFellowTraveler")} open={open} handleClose={handleClose} maxWidth="lg" top="0" icon="close">
            <DialogContent dividers={true}>
                <Grid
                    className="passenger__form"
                    component="form"
                    container
                    item
                    xs={12}
                    rowGap={4}
                    justifyContent="space-between"
                    onSubmit={handleSubmit(handleRegistration, handleError)}
                >
                    {formList.map(item => {
                        return (
                            <Grid key={item?.id} container item md={3.8}>
                                {item.type === "input" && (
                                    <BaseTextField
                                        dir={item.dir}
                                        label={item.label}
                                        name={item.name}
                                        type={item.fieldType}
                                        register={{ ...register(item.name, registerOptions?.[item?.name]) }}
                                        error={errors?.[item?.name]?.message}
                                        radius="10px"
                                        focused={!!getValues(`${item.name}`) || isFocused[item.name]}
                                        onFocus={() => setIsFocused(prev => ({ ...prev, [item.name]: true }))}
                                    />
                                )}

                                {item.type === "select" && (
                                    <AutoCompleteComponent
                                        label={item.label}
                                        options={item.options}
                                        name={item.name}
                                        defaultValue={getValues(`${item?.name}`)}
                                        setInputValue={item.setInputValue}
                                        control={control}
                                        register={registerOptions?.[item?.name]}
                                        errors={errors?.[item?.name]?.message}
                                        width="auto"
                                    />
                                )}

                                {item.type === "date" && (
                                    <SingleDatePicker
                                        label={item.label}
                                        name={`${item.name}`}
                                        size="medium"
                                        isPersianCalendar={item?.isPersianCalendar && locale === "fa"}
                                        defaultValue={getValues(`${item.name}`) ? new DateObject(new Date(getValues(`${item.name}`))) : null}
                                        minDate={item?.minDate}
                                        maxDate={item?.maxDate}
                                        format="YYYY/MM/DD"
                                        control={control}
                                        register={registerOptions[item.name]}
                                        errors={errors?.[item.name]?.message || errors?.[item.name]?.message}
                                    />
                                )}
                            </Grid>
                        );
                    })}

                    <Grid container item md={3.8}>
                        <BaseButton text={t("registerTraveler")} color="primary" size="large" radius="radius-10" loading={loading} />
                    </Grid>
                </Grid>
            </DialogContent>
        </BaseDialog>
    );
}

export default AddPassengerModal;
